import React, { useEffect, useState, useRef } from "react";
import { array, number, bool, node, func } from "prop-types";

import "./srollSnap.m.css";

const ScrollSnap = ({
  children,
  defaultSelection = 0,
  activeIndex,
  isArrow = true,
  leftArrow,
  rightArrow,
  handleNextClick,
  onChange
}) => {
  const noOfItems = children ? children.length : 0;
  const initialIndex = typeof activeIndex === "number" ? activeIndex : defaultSelection || 0;
  const [selectedIndex, setSelectedIndex] = useState(() =>
    Math.max(0, Math.min(initialIndex, Math.max(0, noOfItems - 1)))
  );
  const [imageHeight, setImageHeight] = useState("");
  const scroller = useRef(null);
  const scrollHandlerRef = useRef(null);

  const debounce = (func, ms) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        func(...args);
      }, ms);
    };
  };

  // measure height (use scroller height instead of brittle DOM selector)
  useEffect(() => {
    const setHeight = () => {
      if (scroller.current) setImageHeight(scroller.current.clientHeight / 2 - 90 || "");
      console.log(scroller.current.clientHeight);
    };

    setHeight();
    window.addEventListener("resize", setHeight);
    return () => window.removeEventListener("resize", setHeight);
  }, []);

  useEffect(() => {
    if (!scroller.current) return;
    const handler = debounce(() => {
      if (!scroller.current || noOfItems === 0) return;
      const itemWidth = scroller.current.scrollWidth / noOfItems || scroller.current.clientWidth;
      const index = Math.round(scroller.current.scrollLeft / itemWidth);
      setSelectedIndex(index);
      onChange && onChange(index);
    }, 150);

    scrollHandlerRef.current = handler;
    scroller.current.addEventListener("scroll", handler);
    return () => {
      if (scroller.current && scrollHandlerRef.current) {
        scroller.current.removeEventListener("scroll", scrollHandlerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOfItems, children, onChange]);

  // If parent controls index via activeIndex, update to that
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (typeof activeIndex === "number" && activeIndex !== selectedIndex) {
        updateItemSelection(activeIndex);
        setSelectedIndex(activeIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const smoothScroll = (node, topOrLeft, horizontal) => {
    if (!node) return;
    return node.scrollTo({
      [horizontal ? "left" : "top"]: topOrLeft,
      behavior: "smooth"
    });
  };

  const updateItemSelection = (index) => {
    if (!scroller.current || noOfItems === 0) return;
    const safeIndex = Math.max(0, Math.min(index, noOfItems - 1));
    const itemWidth = scroller.current.scrollWidth / noOfItems || scroller.current.clientWidth;
    const scrollLeft = Math.floor(itemWidth * safeIndex);
    smoothScroll(scroller.current, scrollLeft, true);
  };

  const nextClick = (e) => {
    e && e.preventDefault();
    e && e.stopPropagation();
    if (!scroller.current || noOfItems === 0) return;

    if (selectedIndex < noOfItems - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex);
      onChange && onChange(newIndex);
      handleNextClick && handleNextClick();
    }
  };

  const previousClick = (e) => {
    e && e.preventDefault();
    e && e.stopPropagation();
    if (!scroller.current || noOfItems === 0) return;

    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex);
      onChange && onChange(newIndex);
    }
  };

  const arrowView = () => (
    <div style={{ height: imageHeight }} styleName="srollsnap-arrow-wrapper">
      {selectedIndex !== 0 ? (
        <span onClick={previousClick} styleName="arrow left-arrow" className="left-arrow">
          {leftArrow}
        </span>
      ) : null}

      {selectedIndex < noOfItems - 1 ? (
        <span onClick={nextClick} styleName="arrow right-arrow" className="right-arrow">
          {rightArrow}
        </span>
      ) : null}
    </div>
  );

  return (
    <div>
      <div styleName="wrapper" className="scroll-snap-wrapper">
        <div ref={scroller} styleName="carousel" className="scroll-snap-carousel">
          {children}
        </div>
        {isArrow ? arrowView() : null}
      </div>
    </div>
  );
};

ScrollSnap.propTypes = {
  children: array.isRequired,
  leftArrow: node,
  rightArrow: node,
  defaultSelection: number,
  activeIndex: number, // controlled prop
  isArrow: bool,
  handleNextClick: func,
  onChange: func // parent callback
};

export default ScrollSnap;
